import { useState } from 'react';
import { useTranslations } from '@/i18n/use-translations.tsx';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { Box } from '@mui/material';
import { MdAdminPanelSettings } from 'react-icons/md';
import Widget from '@/components/common/widget.tsx';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import { ModeratorList } from '../grid/moderator-list.tsx';
import { AddFlowModeratorInput } from '../grid/add-flow-moderator.input';
import { IgnorableClickAwayListener } from '@/components/common/ignorable-click-away-listener.tsx';
import useEventMode from '@/components/events/hooks/use-event-mode';
import { isOrganizeMode } from '@/components/events/helpers/mode.helper';
import { useFlowModeratorsSynced } from './use-flow-moderators-synced';
import type { ContributorListDisplayVariant } from '@/components/flows/detail/flow-detail.types';
import { useEventStore } from '@/state/stores/use-event-store';
import { useGetEventSettingsQuery } from '@/state/queries/events/use-get-event-settings';
import { getObjectId } from '@/helpers/object.id.helper';
import { FlowHostMediaDefaultsModal } from '@/components/flows/detail/flow-detail-tabs/flow-host-media-defaults-modal';

export const Moderators = ({
  flow,
  eventSlotId,
  displayVariant,
}: {
  flow: FlowDto;
  eventSlotId?: string;
  displayVariant?: ContributorListDisplayVariant;
}) => {
  const t = useTranslations();
  const [showAddModeratorInput, setShowAddModeratorInput] = useState(false);
  const [hostMediaDefaultsOpen, setHostMediaDefaultsOpen] = useState(false);
  const mode = useEventMode();
  const event = useEventStore((s) => s.event);
  const eventOriginId = event?.origin ? getObjectId(event.origin) : event?.id;
  const { data: eventSettings } = useGetEventSettingsQuery(eventOriginId);
  const { flowForRoles, flowModerators } = useFlowModeratorsSynced(flow, eventSlotId);
  const organizeMode = isOrganizeMode(mode);
  const permissionsLabel = t('media.settings.hosts.permissions');

  return (
    <>
      <Widget
        title={t('common.moderatorCount', { count: flowModerators?.length || 0 })}
        iconButtons={[
          {
            icon: (
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', lineHeight: 1 }}>
                <MdAdminPanelSettings size={20} />
              </Box>
            ),
            label: permissionsLabel,
            onClick: () => setHostMediaDefaultsOpen(true),
            disabled: organizeMode,
            testId: 'flow-host-media-settings-button',
            tooltip: permissionsLabel,
            color: 'secondary',
          },
        ]}
        primaryButton={{
          label: showAddModeratorInput ? t('common.cancel') : t('common.add'),
          disabled: organizeMode,
          onClick: () => setShowAddModeratorInput(!showAddModeratorInput),
          startIcon: showAddModeratorInput ? <CloseIcon /> : <AddOutlinedIcon />,
          testId: 'add-flow-moderator-button',
          id: 'add-flow-moderator-button',
        }}
      >
        {showAddModeratorInput && (
          <IgnorableClickAwayListener
            onClickAway={() => setShowAddModeratorInput(false)}
            ignoreId="add-flow-moderator-button"
          >
            <div>
              <AddFlowModeratorInput
                flow={flowForRoles}
                eventSlotId={eventSlotId}
                onSuccess={() => setShowAddModeratorInput(false)}
              />
            </div>
          </IgnorableClickAwayListener>
        )}
        <ModeratorList
          flow={flowForRoles}
          eventSlotId={eventSlotId}
          displayVariant={displayVariant}
          sx={{ m: '0.4rem 0' }}
        />
      </Widget>
      <FlowHostMediaDefaultsModal
        open={hostMediaDefaultsOpen}
        onClose={() => setHostMediaDefaultsOpen(false)}
        flow={flow}
        eventSettings={eventSettings}
        multiFlow={event?.multiFlow === true}
      />
    </>
  );
};
